import { RefObject, startTransition, useEffect, useState } from "react";

interface IViewportSpy extends Pick<IntersectionObserverInit, "rootMargin" | "threshold"> {
    visibleOnce?: boolean;
    onIntersecting?: (isIntersecting: boolean) => void;
    /** Must be stable. */
    shouldIgnoreChange?: () => boolean;
    /** Must be stable. */
    subscribeToRecheck?: (recheck: () => void) => () => void;
}

const useViewportSpy = <T extends HTMLElement | null>(elementRef: RefObject<T>, options: IViewportSpy = {}) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const { visibleOnce = false, threshold = 0, rootMargin = "0px", onIntersecting, shouldIgnoreChange, subscribeToRecheck } = options;
    const isDone = visibleOnce && isVisible;

    useEffect(() => {
        const element = elementRef.current;

        if (!element || isDone) {
            return;
        }

        const observer = new window.IntersectionObserver(
            entries =>
                entries.forEach(({ isIntersecting }) => {
                    if (shouldIgnoreChange?.()) {
                        return;
                    }

                    onIntersecting?.(isIntersecting);
                    startTransition(() => {
                        setIsVisible(isIntersecting);
                    });
                }),
            { threshold, rootMargin }
        );

        observer.observe(element);

        const unsubscribeFromRecheck = subscribeToRecheck?.(() => {
            observer.unobserve(element);
            observer.observe(element);
        });

        return () => {
            unsubscribeFromRecheck?.();
            observer.disconnect();
        };
    }, [elementRef, isDone, rootMargin, threshold, onIntersecting, shouldIgnoreChange, subscribeToRecheck]);

    return isVisible;
};

export default useViewportSpy;
